import { IWithAudioEffects } from './common'

interface IPosition {
  x: number
  y: number
}

interface IFlip {
  x: boolean
  y: boolean
}

export enum MediaStatus {
  PENDING = 'pending',
  OK = 'ok',
  ERROR = 'error'
}

export type TMediaStatus = MediaStatus

export type TTextAlign = 'left' | 'center' | 'right'

/**
 * For text items, `IMediaItem.width` is the wrap width and `IMediaItem.height`
 * is engine-derived from the rendered line count. `text` may contain `\n` for
 * explicit line breaks.
 */
export interface ITextData {
  text: string
  fontPostScriptName: string
  fontPath: string
  fontSize: number
  fillColor: string
  strokeColor: string
  strokeThickness: number
  lineHeight: number
  textAlign: TTextAlign
  /**
   * When `true` (default), text grows with content. When `false`, the
   * media item's `width` is a fixed wrap width and the renderer word-wraps
   * to it. Flipped to `false` when the user side-resizes the text frame.
   */
  isAutoWidth: boolean
}

export interface IMediaItemMeta extends IWithAudioEffects {
  path?: string | null
  originalHeight?: number
  originalWidth?: number
  originalDuration?: number
  trimStart?: number
  previewPath?: string
  status?: TMediaStatus
  volume?: number
  textData?: ITextData
  assetParentId?: string
  playbackSpeed?: number
}

export type TAiMediaItemType = 'aiImage' | 'aiVideo' | 'aiAvatar'

export type TMediaItemType = 'image' | 'video' | 'text' | TAiMediaItemType

export type TAiMediaSourceType = 'ai-image' | 'ai-video'

export interface IMediaItem {
  id: string
  assetId: string
  type: TMediaItemType
  height: number
  width: number
  rotation: number
  duration: number
  startTime: number
  zIndex: number
  coordinates: IPosition
  flip?: IFlip
  meta: IMediaItemMeta
}

export type TPartialMediaItem = Partial<Omit<IMediaItem, 'meta'>> & {
  meta: Partial<IMediaItemMeta>
}

export interface IMyFilesMediaItem {
  id: number
  folderId: number
  createdAt: string
  updatedAt: string
  fileName: string
  filePath: string
  downloadPath: string
  fileSize: number
  mime: string
  width: number
  height: number
  thumbnailPath: string
  duration?: number
  thumbnailVideo?: string
  webpPath?: string
  sourceType?: TAiMediaSourceType
  assetId?: string
  // Avatars are stored as `ai-video` in My Files; the host marks them with
  // `meta.aiAvatarProjectId` (the same discriminator as the in-app ai-video API),
  // so a selected avatar can be typed `aiAvatar` instead of `aiVideo`.
  meta?: { aiAvatarProjectId?: number }
}

export type TMyFilesMediaItems = IMyFilesMediaItem[]

export interface IBaseMediaItem {
  id?: string
  mediaId?: number
  assetId?: string
  fileName: string
  type: TMediaItemType
  originalWidth: number
  originalHeight: number
  path?: string | null
  previewPath?: string
  duration: number
  status?: MediaStatus
  assetParentId?: string
  thumbnail?: string | null
  imageUrl?: string | null
}

export interface IBaseUploadedMediaItem {
  id: number
  filePath: string
}

export interface IUploadMediaItem {
  id: string | number
  file: File
  type: TMediaItemType
}
